import * as React from 'react';
import {useSelector} from "react-redux";
import {has} from "lodash";
import widgets from "../components/widget";
import MainContainer from "../components/MainContainer";
import cards from "../components/Cards";
import {Divider, FooterContainer, ColumnContainer, RowContainer} from "../components/Containers";
import {Sidebar, StickyContainer} from "../components/Content";
import LoadMoreArticlesButton from "../components/LoadMoreArticlesButton";
import {templates} from "../config";
import {ArticleEntity} from "../contract/article";
import useCurrentRoute from "../hooks/useCurrentRoute";
import useTemplate from "../hooks/useTemplate";
import useMedia from "../hooks/useMedia";
import {RootState} from "../redux/store";
import {INITIAL_ROUTE} from "../hooks/useRoutes";

const NewsIndex: React.FC = (): React.ReactElement => {
  const pathname = useCurrentRoute();
  const {onMobile, onTablet} = useMedia();
  const {
    articles_visible,
    breakpoints,
    layout,
    theme,
    enabled_widgets,
  } = useSelector((state: RootState) => {
    const {config, routes, theme} = state.application;
    const route = has(routes, pathname) ? routes[pathname] : INITIAL_ROUTE;
    const {articles_visible} = route;
    const {template, widgets} = config;
    const {breakpoints} = templates.find(({id}: {id: string}) => id === template);

    return {
      articles_visible,
      breakpoints,
      layout: template,
      theme,
      enabled_widgets: widgets,
    }
  });
  const {sections, maxColumns, containerStyles, LAYOUT_CONSTANTS} = useTemplate(breakpoints);

  // bootstrap
  const {CONTENT_MAX_WIDTH, FORM_ROW_PADDING, GUTTER_SIZE}: {
    CONTENT_MAX_WIDTH: number;
    FORM_ROW_PADDING: React.ReactText|string,
    GUTTER_SIZE: number
  } = LAYOUT_CONSTANTS;
  const hasSidebar = (!onMobile && !onTablet && maxColumns > 3);
  const sidebarWidth = `calc(${(1/maxColumns) * 100}% - calc(${(maxColumns - 1) / maxColumns} * ${FORM_ROW_PADDING}))`;

  return (
    <MainContainer role="main" theme={theme} style={containerStyles}>
      {(sections || []).map(({template, columns, entries}, index = 0) => {
        if (!has(cards, template)) return;

        const content = entries.map((entry: ArticleEntity, i: number) => (
          React.createElement(
            cards[template],
            {
              content: entry || {},
              final: (i === entries.length - 1),
              index: i + 1,
              columns: entries.length < columns ? entries.length : columns,
              maxWidth: CONTENT_MAX_WIDTH,
              priority: -1, // sections.length - index, //
              loading: !(articles_visible || []).find(id => id === String(entry?.id)),
              key: entry?.slug || `${index}-${i}`,
            }
          )
        ));

        if (index < sections.length - 1) {
          return (
            <RowContainer key={`${index}-${template}`}>
              {content}
            </RowContainer>
          )
        } else {
          const final = [];
          if (!onMobile && (layout !== 'headlines')) final.push(
            <Divider key={`0-${template}`} style={{margin: `${FORM_ROW_PADDING} 0 ${GUTTER_SIZE}px 0`}} />
          );

          final.push(React.createElement(
            hasSidebar ? RowContainer : ColumnContainer,
            {
              key: `${index}-${template}`,
              style: {flexFlow: hasSidebar ? 'row nowrap' : 'column nowrap'}
            },
            hasSidebar ? (
              <>
                <ColumnContainer style={{marginRight: `calc(${GUTTER_SIZE / 2}px)`, width: '100%'}}>
                  {content}
                </ColumnContainer>

                <Sidebar
                  style={{
                    minWidth: sidebarWidth,
                  }}
                >
                  <StickyContainer style={{top: `calc(${GUTTER_SIZE / 2}px)`}}>
                    {enabled_widgets.map((widget, i) => (
                      React.createElement(
                        widgets[widget],
                        {
                          className: `f-5 theme--primary-color theme--bg-card-color theme--thumbnail-border-radius`,
                          key: `widget-${i}-${widget}`,
                          style: {
                            opacity: 0.33,
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: `calc(${GUTTER_SIZE / 2}px)`
                          }
                        },
                        widget
                      )
                    ))}
                  </StickyContainer>
                </Sidebar>
              </>
            ) : content
          ));

          return final;
        }
      })}

      <FooterContainer
        style={{
          justifyContent: 'center',
          paddingRight: hasSidebar ? sidebarWidth : 0,
          marginRight: hasSidebar ? `calc(${GUTTER_SIZE / 2}px)` : 0
        }}
      >
        <LoadMoreArticlesButton />
      </FooterContainer>
    </MainContainer>
  )
}

export default NewsIndex;
